import { Component, EventEmitter, Input, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatestWith, map, Observable, Subject, take, takeUntil, tap } from 'rxjs';

import { UserDetailsDto } from '../../../../../common/dtos/user-details.dto';
import { ACCOUNT_ID_PARAM } from '../../../../constants/application-routes';
import { AdobeLaunchService } from '../../../../services/adobe-launch/adobe-launch.service';
import { updateRemoveUserStepperSelectedUser } from '../../../../store/actions/remove-user-stepper-flow.action';
import * as accountPermissionsSelector from '../../../../store/selectors/account-permissions.selector';
import * as grantRevokePermissionsSelector from '../../../../store/selectors/grant-revoke-permissions.selector';
import accountPermissionUtils from '../../../../utils/account-permission/account-permission.util';
import { AdobeTagging } from '../../../../utils/decorators/dev/adobe-launch';
import { adobeConfigData } from './raf-select-user-step-adobe';
import { AdobeAnalyticsProcessStep } from '../../../../services/adobe-launch/adobe-launch.process';
import { ModalDialogComponent, ModalDialogModule } from '@vg-constellation/angular-20/modal-dialog';
import { ContentUtil } from 'src/app/utils/content/content.util';
import { ClientSegmentFacadeService } from 'src/app/services/client-segment-observer/client-segment.service';
import { ClientSegmentContentDTO } from 'src/common/dtos/client-segment.dto';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { AccessTypeSelection } from 'src/common/enums/access-type-selection.enum';
import { environment } from '../../../../../environments/environment';
import { Content } from 'src/common/interfaces/content.interface';
import { Unsubscribe } from 'src/app/utils/decorators/dev';
import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { LinkModule } from '@vg-constellation/angular-20/link';
import { BannerModule } from '@vg-constellation/angular-20/banner';
import { InternalCalloutModule } from '@vg-constellation/angular-20/internal-callout';
import { RadioModule } from '@vg-constellation/angular-20/radio';


@AdobeTagging(adobeConfigData)
@Unsubscribe
@Component({
    selector: 'zci-raf-select-user-step',
    templateUrl: './raf-select-user-step.component.html',
    styleUrls: ['./raf-select-user-step.component.scss'],
    imports: [
        AsyncPipe,
        NgFor,
        ReactiveFormsModule,
        ModalDialogModule,
        RadioModule,
        BannerModule,
        InternalCalloutModule,
        LinkModule,
    ],
})
export class RafSelectUserStepComponent
    implements OnInit, OnDestroy {
    protected readonly adobeLaunchService = inject(AdobeLaunchService);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly store = inject(Store);
    private readonly logger = inject(LoggerService);
    private readonly clientSegmentFacadeService = inject(ClientSegmentFacadeService);

    @Input() nextStepClicked: EventEmitter<any> = new EventEmitter<any>();
    @Input() nextStepReady: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Input() locationChanged: EventEmitter<any>;

    content: Content = ContentUtil.content;
    grantorContent = ContentUtil.grantorFragmentContent;

    @ViewChild('selectUserModalDialogue', { static: false })
    selectUserModalDialogue!: ModalDialogComponent;

    readonly infoWaveUrl: string = environment.INFO_WAVE_URL;
    readonly isInternal: boolean = environment.isInternal;

    users$: Observable<UserDetailsDto[]>;

    radioGroupId: string = 'user-selection-group';

    selectUserFormControlName: string = 'selectedUser';
    selectUserFormGroup: FormGroup = new FormGroup({
        [this.selectUserFormControlName]: new FormControl(null, [Validators.required]),
    });

    constructUserName: (user: UserDetailsDto) => string = accountPermissionUtils.constructUserName;
    errDescription: string;
    protected readonly AdobeAnalyticsProcessStep = AdobeAnalyticsProcessStep;
    private destroy$ = new Subject<void>();

    get selectUserFormControl(): AbstractControl {
        return this.selectUserFormGroup.get(this.selectUserFormControlName);
    }

    get infowaveBoxContents(): string[] {
        const infoWaveBox = this.content?.removeAccessFlow?.selectUser?.infoWaveBox;
        return typeof infoWaveBox === 'string'
            ? infoWaveBox.split(`{{infoWaveLink}}`).map((part) => part.trim())
            : [];
    }

    /**
     * Component lifecycle
     */
    ngOnInit(): void {
        const accountId = this.activatedRoute.snapshot.params[ACCOUNT_ID_PARAM];
        const existingUsers$ = this.store.select(
            accountPermissionsSelector.selectGrantedUsersOnAccount(
                accountId,
                this.logger.warn,
            ),
        );
        const selectedUser$: Observable<UserDetailsDto> = this.store.select(
            grantRevokePermissionsSelector.selectRemoveUserStepperSelectedUser,
        );

        this.clientSegmentFacadeService.clientSegmentContent$
            .pipe(takeUntil(this.destroy$))
            .subscribe((content: ClientSegmentContentDTO) => {
                this.errDescription =
                    this.grantorContent.edit_remove_user_access.removeThisPersonContactInfo.text1.replace(
                        '${phoneNumber}',
                        content?.phoneNumber,
                    );
            });

        this.users$ = existingUsers$.pipe(
            combineLatestWith(selectedUser$),
            tap(this.patchFormValueWithSelectedUser.bind(this)),
            map(([existingUsers]) =>
                [...existingUsers].sort((u1, u2) =>
                    /* istanbul ignore next */
                    this.constructUserName(u1) > this.constructUserName(u2) ? 1 : -1,
                ),
            ),
            take(1),
        );

        this.nextStepClicked.subscribe(this.onNextStepLoad.bind(this));
    }

    onUserSelect(user: UserDetailsDto): void {
        if (user.accessLevel === AccessTypeSelection.FULL_AUTHORITY) {
            this.selectUserModalDialogue.openModalDialog();
        }
    }

    quit(): void {
        if (this.selectUserModalDialogue) {
            this.selectUserModalDialogue.closeDialogModal();
        }
    }

    /**
     * Moves the ui flow to the next step.
     * If the selected user has Full Authority access, the modal is shown and the stepper
     * is notified not to advance (emit false resets the stepper's waiting/loading state).
     * For all other access levels the flow advances normally.
     */
    onNextStepLoad(): void {
        this.selectUserFormControl.markAsDirty();
        if (this.selectUserFormControl.valid) {
            const selectedUser: UserDetailsDto = this.selectUserFormControl.value;
            if (selectedUser?.accessLevel === AccessTypeSelection.FULL_AUTHORITY) {
                this.selectUserModalDialogue.openModalDialog();
                this.nextStepReady.emit(false);
                return;
            }
            this.store.dispatch(
                updateRemoveUserStepperSelectedUser({
                    selectedUser,
                }),
            );
            this.nextStepReady.emit(true);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private patchFormValueWithSelectedUser([existingUsers, selectedUser]: [
        UserDetailsDto[],
        UserDetailsDto,
    ]): void {
        if (selectedUser) {
            const selectedUserToBePatched = existingUsers.find(
                (user) => user.clientPoid === selectedUser.clientPoid,
            );
            if (selectedUserToBePatched) {
                this.selectUserFormControl.patchValue(selectedUserToBePatched);
                return;
            }
        }
    }
}
